import React from "react";
import ReactDOM from "react-dom/client";
import { onlineManager } from "@tanstack/react-query";
import { theme } from "./theme";
import { AppConfigurationProvider, bool, Fonts } from "am-tax-fe-core";
import { ChakraProvider } from "@chakra-ui/react";
import { Middleware } from "openapi-fetch";
import { configureApiClient } from "./api";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PageRoutes } from "./pages";

//import the fe-core style sheet (fe-core bundles up all the stylesheets that are included from third party libs and exports them as a single css file)
import "am-tax-fe-core/index.css";
import { getAppState, useAppState } from "./state/useAppState";
import AuthSetup from "./components/AuthSetup";
import ErrorModal from "./components/ErrorModal";

function configureHttpClient(baseUrl: string) {
    const tokenMiddleware: Middleware = {
        async onRequest({ request }) {
            const token = getAppState().token;
            if (token) {
                request.headers.append("Authorization", `Bearer ${token}`);
            }
            return request;
        },
    };

    const errorHandlingMiddleware: Middleware = {
        async onResponse({ response }) {
            if (!response.bodyUsed) {
                // Clone the response to avoid consuming the original stream
                const clonedResponse = response.clone();
                useAppState.getState().processResponse(clonedResponse);
            }
        },
    };

    configureApiClient(baseUrl, [tokenMiddleware, errorHandlingMiddleware]);
}

(async function () {
    if (bool(import.meta.env.VITE_BYPASS_AUTH) || bool(import.meta.env.VITE_MOCK_PARTIAL_BACKEND) || bool(import.meta.env.VITE_MOCK_ENTIRE_BACKEND)) {
        // this probably seems backwards, but we want to tell react query to act like it is online even if it is offline
        onlineManager.setOnline(true);

        const setup = await import("./api/msw/setupForBrowser");
        const { getWorker } = setup;
        const worker = await getWorker();
        await worker.start({ onUnhandledRequest: "bypass" });
    }

    const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

    root.render(
        <React.StrictMode>
            <ChakraProvider theme={theme} resetCSS>
                <Fonts />
                <AppConfigurationProvider
                    envJsonUrl={"/env.json"}
                    byPassAuth={bool(import.meta.env.VITE_BYPASS_AUTH) || bool(import.meta.env.VITE_MOCK_ENTIRE_BACKEND)}
                    unauthenticatedLandingPage={<PageRoutes />}
                    onApiContextInit={configureHttpClient}
                    enableAutoLogout={!bool(import.meta.env.VITE_DISABLE_AUTO_LOGOUT)}
                    enableAppInsights={bool(import.meta.env.VITE_ENABLE_APP_INSIGHTS)}
                    version={{
                        ver: import.meta.env.VITE_BUILD_COMMIT,
                        build: import.meta.env.VITE_BUILD_NUMBER,
                    }}
                >
                    <AuthSetup>
                        <PageRoutes />
                        <ErrorModal />
                    </AuthSetup>
                    {bool(import.meta.env.VITE_ENABLE_REACT_QUERY_DEV_TOOLS) && <ReactQueryDevtools initialIsOpen={false} />}
                </AppConfigurationProvider>
            </ChakraProvider>
        </React.StrictMode>,
    );
})();

